import { useCallback, useEffect, useState } from 'react'
import {
  getNotifications,
  markNotificationRead,
} from '@services/api/notification.api'
import {
  NotificationPriority,
  type Notification,
} from '@dataTypes/notification.types'

const ALERTS_LIMIT = 20

export type UseOverviewAlertsResult = {
  critical: Notification[]
  urgent: Notification[]
  criticalCount: number
  urgentCount: number
  loading: boolean
  error: string | null
  refetch: () => void
  markRead: (notificationId: string) => Promise<void>
}

export function useOverviewAlerts(
  enabled = true,
  queued = false,
): UseOverviewAlertsResult {
  const waiting = queued && !enabled
  const [critical, setCritical] = useState<Notification[]>([])
  const [urgent, setUrgent] = useState<Notification[]>([])
  const [loading, setLoading] = useState(enabled || waiting)
  const [error, setError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  const refetch = useCallback((): void => {
    setReloadKey((k) => k + 1)
  }, [])

  useEffect(() => {
    if (!enabled) {
      setLoading(waiting)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)

    Promise.all([
      getNotifications({
        status: 'unread',
        priority: NotificationPriority.CRITICAL,
        limit: ALERTS_LIMIT,
      }),
      getNotifications({
        status: 'unread',
        priority: NotificationPriority.URGENT,
        limit: ALERTS_LIMIT,
      }),
    ])
      .then(([criticalItems, urgentItems]) => {
        if (cancelled) return
        setCritical(criticalItems)
        setUrgent(urgentItems)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setError(
          e instanceof Error ? e.message : 'Failed to load overview alerts',
        )
        setCritical([])
        setUrgent([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [enabled, reloadKey, waiting])

  const markRead = useCallback(
    async (notificationId: string): Promise<void> => {
      await markNotificationRead(notificationId)
      setCritical((prev) =>
        prev.filter(
          (n) =>
            n.notification_id !== notificationId && n.id !== notificationId,
        ),
      )
      setUrgent((prev) =>
        prev.filter(
          (n) =>
            n.notification_id !== notificationId && n.id !== notificationId,
        ),
      )
    },
    [],
  )

  return {
    critical,
    urgent,
    criticalCount: critical.length,
    urgentCount: urgent.length,
    loading: loading || waiting,
    error,
    refetch,
    markRead,
  }
}
